// Personnel compliance documents (Session AL, Item 3). Office/admin. DBS checks,
// data-protection certificates and similar documents held against a named individual,
// grouped by person, with an expiry indicator, a "View in Drive" link and an authenticated
// Download, plus an Add document form and a Delete (the record only; the Drive file is kept).
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Plus } from "lucide-react";
import {
  api,
  ApiError,
  type PersonalComplianceList,
  type PersonalDocDto,
  type PersonalDocStatus,
  type PersonalDocType,
  type PersonalComplianceCreateInput,
} from "../../lib/api";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { ListToolbar } from "../../components/ListToolbar";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { statusEntry } from "../../lib/statusRegistry";
import { formatDate } from "../../lib/format";
import { useListSearch } from "../../lib/useListSearch";
import { useSortableColumn } from "../../lib/useSortableColumn";

const DOC_TYPES: Array<{ value: PersonalDocType; label: string }> = [
  { value: "DBS_CHECK", label: "DBS check" },
  { value: "DATA_PROTECTION_CERT", label: "Data protection certificate" },
  { value: "INSURANCE_CERT", label: "Insurance certificate" },
  { value: "OTHER", label: "Other" },
];

const STATUS_LABEL: Record<PersonalDocStatus, string> = {
  valid: "Valid",
  expiring: "Expiring soon",
  expired: "Expired",
  "no-expiry": "No expiry",
};

const inputCls = "rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";

// Client-side search and sort over the groups the (unchanged) server returned (design system Session 7a).
// The search term is the URL's ?q=. A person's name keeps all of their documents in view; otherwise a
// document stays when its type or status matches. Groups sort by name or by the soonest expiry they hold.
const SORT_OPTIONS = [
  { key: "person", label: "Person" },
  { key: "expiry", label: "Soonest expiry" },
];

function soonestExpiry(g: PersonalComplianceList["groups"][number]): number {
  const times = g.documents.map((d) => (d.expiryDate ? Date.parse(d.expiryDate) : NaN)).filter((t) => !Number.isNaN(t));
  return times.length ? Math.min(...times) : Number.POSITIVE_INFINITY;
}

function narrowGroups(groups: PersonalComplianceList["groups"], search: string): PersonalComplianceList["groups"] {
  const q = search.trim().toLowerCase();
  if (!q) return groups;
  const out: PersonalComplianceList["groups"] = [];
  for (const g of groups) {
    if (g.personName.toLowerCase().includes(q)) {
      out.push(g);
      continue;
    }
    const docs = g.documents.filter(
      (d) => d.documentTypeLabel.toLowerCase().includes(q) || (STATUS_LABEL[d.status] ?? d.status).toLowerCase().includes(q),
    );
    if (docs.length) out.push({ ...g, documents: docs });
  }
  return out;
}

export function PersonnelCompliancePage() {
  const [data, setData] = useState<PersonalComplianceList | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<PersonalDocDto | null>(null);
  const [delBusy, setDelBusy] = useState(false);
  const [delErr, setDelErr] = useState<string | null>(null);
  // Card list below md, table otherwise -- decided once at mount (Session AGZ's mechanism).
  // jsdom has no matchMedia, so every existing test still renders the table it pins.
  const [mobileCards] = useState(initialMobileLayout);

  const load = useCallback(() => {
    setLoadError(false);
    api.personnelCompliance.list().then(setData).catch(() => setLoadError(true));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("personnel-compliance");
  const groups = data ? narrowGroups(data.groups, search) : null;
  const sortedGroups = groups
    ? sort.sortKey === "person"
      ? [...groups].sort((a, b) => a.personName.localeCompare(b.personName) * (sort.sortDir === "desc" ? -1 : 1))
      : sort.sortKey === "expiry"
        ? [...groups].sort((a, b) => (soonestExpiry(a) - soonestExpiry(b) || 0) * (sort.sortDir === "desc" ? -1 : 1))
        : groups
    : null;
  const totalDocs = data ? data.groups.reduce((n, g) => n + g.documents.length, 0) : 0;
  const shownDocs = groups ? groups.reduce((n, g) => n + g.documents.length, 0) : 0;

  async function confirmDelete() {
    if (!deleting) return;
    setDelBusy(true);
    setDelErr(null);
    try {
      await api.personnelCompliance.remove(deleting.id);
      setDeleting(null);
      load();
    } catch (e) {
      setDelErr(e instanceof ApiError ? e.message || e.code : "Could not delete this document. Please try again.");
    } finally {
      setDelBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6">
      <PageHeader
        title="Personnel compliance"
        className="mb-4"
        subtitle="DBS checks, data protection and similar documents held against a named individual, so they can be retrieved and forwarded to a customer or auditor on request."
        actions={
          <Button onClick={() => setAdding(true)} icon={<Plus aria-hidden size={15} />}>
            Add document
          </Button>
        }
      />

      {data !== null && data.groups.length > 0 && (
        <ListToolbar
          className="mb-4"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search person, document type or status..."
          searchLabel="Search personnel documents"
          resultCount={{ shown: shownDocs, total: totalDocs, noun: "documents" }}
          sort={{ state: sort, options: SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
          onClear={() => setSearch("")}
        />
      )}

      {loadError ? (
        <p role="alert" className="rounded-ele border border-ele-danger bg-ele-surface p-6 text-center text-ele-strong">
          Could not load personnel compliance documents. Please try again.
        </p>
      ) : data === null || sortedGroups === null ? (
        <Loader label="Loading personnel documents..." />
      ) : data.groups.length === 0 ? (
        <EmptyState message="No documents on file yet." />
      ) : sortedGroups.length === 0 ? (
        <EmptyState role="status" message={`No personnel documents match "${search.trim()}".`} />
      ) : (
        <div className="space-y-5">
          {sortedGroups.map((g) => (
            <section key={g.personName} className="overflow-hidden rounded-ele border border-ele-purple-accent">
              <h2 className="bg-ele-inset px-4 py-2.5 font-heading text-lg tracking-wide text-ele-strong">{g.personName}</h2>
              {mobileCards ? (
                /* Session ALY mobile card list. At 375px the fixed-layout table redistributes its
                   surviving columns (Document 28%, Status 20%, Actions 36% of the section's ~341px)
                   to roughly 114/81/146px; a 27-character label like "Data protection certificate"
                   has only ~82px of content room after padding, well short of what it needs -- the
                   same "reduced to nothing useful" truncation as the Invoices bug. The card stacks
                   instead: the document type never truncates. Status keeps its inline expiry note,
                   and View in Drive / Download -- "the point of a compliance check" per this page's
                   own design note -- come along. Issued and Delete stay OFF the card: this page's
                   existing design already hides them below md on the desktop table on purpose
                   (Issued drops on mobile; Delete is a rarer, destructive action hidden on a small
                   screen), and the card keeps that same call rather than silently reversing it. */
                <MobileCardList label={`${g.personName} compliance documents`}>
                  {g.documents.map((d) => (
                    <MobileListCard
                      key={d.id}
                      primary={d.documentTypeLabel}
                      right={
                        <span className="flex flex-col items-end gap-1 text-right">
                          <StatusPill word={STATUS_LABEL[d.status] ?? d.status} tone={statusEntry("certificate", d.status).tone} />
                          {d.expiryDate ? <span className="text-xs text-ele-body">({formatDate(d.expiryDate)})</span> : null}
                        </span>
                      }
                      actions={
                        d.driveFileUrl || d.hasDownload ? (
                          <>
                            {d.driveFileUrl ? (
                              <a
                                href={d.driveFileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex min-h-11 items-center font-menu text-xs tracking-wide text-ele-blue hover:underline"
                              >
                                View in Drive
                              </a>
                            ) : null}
                            {d.hasDownload ? (
                              <a
                                href={api.personnelCompliance.downloadHref(d.id)}
                                className="flex min-h-11 items-center font-menu text-xs tracking-wide text-ele-green hover:underline"
                              >
                                Download
                              </a>
                            ) : null}
                          </>
                        ) : undefined
                      }
                    />
                  ))}
                </MobileCardList>
              ) : (
              <table className="ele-list-table w-full text-left text-sm">
                {/* No detail page: below md the budget is Document (identifier) + Status
                    (which already carries the expiry date inline) + the single most important
                    action. View in Drive / Download are kept (accessing the actual document is
                    the point of a compliance check); Delete is a rarer, destructive action and
                    is hidden on a small screen (rule 4). Issued drops on mobile. */}
                <colgroup>
                  <col style={{ width: "28%" }} />
                  <col className="max-md:hidden" style={{ width: "16%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "36%" }} />
                </colgroup>
                <thead className="bg-ele-surface font-menu text-xs uppercase tracking-wide text-ele-blue">
                  <tr>
                    <th className="px-4 py-2">Document</th>
                    <th className="max-md:hidden px-4 py-2">Issued</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {g.documents.map((d) => (
                    <tr key={d.id} className="border-t border-ele-hairline">
                      <td className="px-4 py-2 text-ele-strong">{d.documentTypeLabel}</td>
                      <td className="max-md:hidden px-4 py-2 text-ele-body">{formatDate(d.issuedDate)}</td>
                      <td className="px-4 py-2">
                        <StatusPill word={STATUS_LABEL[d.status] ?? d.status} tone={statusEntry("certificate", d.status).tone} />
                        {d.expiryDate ? <span className="ml-1 text-ele-body">({formatDate(d.expiryDate)})</span> : null}
                      </td>
                      <td className="cell-actions px-4 py-2">
                        <div className="flex flex-wrap items-center gap-3">
                          {d.driveFileUrl ? (
                            <a href={d.driveFileUrl} target="_blank" rel="noopener noreferrer" className="font-menu text-xs tracking-wide text-ele-blue hover:underline">
                              View in Drive
                            </a>
                          ) : null}
                          {d.hasDownload ? (
                            <a href={api.personnelCompliance.downloadHref(d.id)} className="font-menu text-xs tracking-wide text-ele-green hover:underline">
                              Download
                            </a>
                          ) : null}
                          <button
                            onClick={() => {
                              setDelErr(null);
                              setDeleting(d);
                            }}
                            className="max-md:hidden font-menu text-xs tracking-wide text-ele-danger hover:underline"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              )}
            </section>
          ))}
        </div>
      )}

      {adding && (
        <AddDocumentModal
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            load();
          }}
        />
      )}

      <ConfirmDeleteModal
        open={deleting != null}
        title="Delete this document record?"
        message="This removes the compliance record from the platform. The source file in Google Drive is left in place and is not deleted."
        busy={delBusy}
        error={delErr}
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}

function AddDocumentModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    personName: "",
    documentType: "DBS_CHECK" as PersonalDocType,
    issuedDate: "",
    expiryDate: "",
    driveFileId: "",
    driveFileUrl: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const set =
    (name: keyof typeof form) =>
    (e: { target: { value: string } }) =>
      setForm((f) => ({ ...f, [name]: e.target.value }));

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!form.personName.trim() || !form.issuedDate || !form.driveFileId.trim()) {
      setErr("Person, issue date and a Drive file id are required.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const body: PersonalComplianceCreateInput = {
        personName: form.personName.trim(),
        documentType: form.documentType,
        issuedDate: form.issuedDate,
        expiryDate: form.expiryDate || null,
        driveFileId: form.driveFileId.trim(),
        driveFileUrl: form.driveFileUrl.trim() || null,
        notes: form.notes.trim() || null,
      };
      await api.personnelCompliance.create(body);
      onSaved();
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message || e2.code : "Could not save the document. Please try again.");
      setBusy(false);
    }
  }

  return (
    <ModalShell ariaLabel="Add compliance document" onClose={() => !busy && onClose()} width="max-w-lg" margin="my-8" panelClassName="p-6">
      <form onSubmit={save}>
        <h2 className="mb-4 font-heading text-2xl tracking-wide text-ele-strong">Add compliance document</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="sm:col-span-2">
            <span className={labelCls}>Person</span>
            <input aria-label="Person" value={form.personName} onChange={set("personName")} placeholder="e.g. Adrian Baker" className={`${inputCls} w-full`} />
          </label>
          <label>
            <span className={labelCls}>Document type</span>
            <select aria-label="Document type" value={form.documentType} onChange={set("documentType")} className={`${inputCls} w-full`}>
              {DOC_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className={labelCls}>Issue date</span>
            <input aria-label="Issue date" type="date" value={form.issuedDate} onChange={set("issuedDate")} className={`${inputCls} w-full`} />
          </label>
          <label>
            <span className={labelCls}>Expiry date (optional)</span>
            <input aria-label="Expiry date" type="date" value={form.expiryDate} onChange={set("expiryDate")} className={`${inputCls} w-full`} />
          </label>
          <label>
            <span className={labelCls}>Drive file id</span>
            <input aria-label="Drive file id" value={form.driveFileId} onChange={set("driveFileId")} placeholder="Google Drive file id" className={`${inputCls} w-full`} />
          </label>
          <label className="sm:col-span-2">
            <span className={labelCls}>Drive link (optional, for View in Drive)</span>
            <input aria-label="Drive link" value={form.driveFileUrl} onChange={set("driveFileUrl")} placeholder="https://drive.google.com/file/d/.../view" className={`${inputCls} w-full`} />
          </label>
          <label className="sm:col-span-2">
            <span className={labelCls}>Notes (optional)</span>
            <textarea aria-label="Notes" value={form.notes} onChange={set("notes")} rows={2} className={`${inputCls} w-full`} />
          </label>
        </div>
        {err && (
          <p role="alert" className="mt-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
            {err}
          </p>
        )}
        <div className={`mt-4 gap-2 ${MODAL_ACTIONS}`}>
          <button type="button" onClick={onClose} className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong ${MODAL_ACTION_BTN}`}>
            Cancel
          </button>
          <Button type="submit" disabled={busy} className={MODAL_ACTION_BTN}>
            {busy ? "Saving..." : "Add document"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}
